/*
 * The Databases tab: every database this org is actively monitoring, plus
 * what's coming next.
 *
 * Supabase and Azure are both real, live data once their credentials are set
 * on the deployment (see README.md). Azure covers three kinds of store:
 * PostgreSQL flexible servers (redteam-ip's Tribunal databases), storage
 * accounts holding Table Storage tables (f3insights.com's event registrations
 * and leads), and Azure SQL databases. Each card says which repository owns
 * it, taken from the resource's own `repo` tag rather than a list kept here.
 * AWS Postgres is not wired up yet and is shown as an explicit placeholder
 * rather than silently absent, so nobody has to wonder whether it was
 * forgotten.
 */
import { useEffect, useState } from 'react'
import { getDatabases } from '../lib/api'
import { rel } from '../lib/format'
import {
  ApiError,
  type AzureServerSummary,
  type AzureSqlDatabaseSummary,
  type AzureTableStoreSummary,
  type DatabasesResponse,
  type SupabaseProjectSummary
} from '../types'
import { ExternalLink, Sync, Warning } from './Icons'

const PLACEHOLDERS = [
  {
    key: 'aws',
    title: 'AWS Postgres',
    note: 'Staging and development test databases on AWS RDS. Not yet monitored.'
  }
]

const UNHEALTHY = new Set(['unhealthy', 'failed', 'restore failed', 'pause failed'])
const DORMANT = new Set(['paused', 'removed'])

type StatusTone = 'success' | 'failure' | 'neutral' | 'pending'

function statusClass(p: SupabaseProjectSummary): StatusTone {
  if (p.healthy) return 'success'
  if (UNHEALTHY.has(p.statusLabel)) return 'failure'
  if (DORMANT.has(p.statusLabel)) return 'neutral'
  return 'pending'
}

/* Azure's provisioning states are a different vocabulary from Supabase's, but
 * they collapse to the same four tones: running, broken, deliberately off, or
 * mid-transition. */
const AZURE_STOPPED = new Set(['stopped', 'disabled', 'dropping'])

function azureStatusClass(s: AzureServerSummary): StatusTone {
  if (s.healthy) return 'success'
  if (AZURE_STOPPED.has(s.statusLabel)) return 'neutral'
  if (s.statusLabel === 'unknown') return 'failure'
  return 'pending'
}

function tableStatusClass(t: AzureTableStoreSummary): StatusTone {
  if (t.healthy) return 'success'
  if (t.statusLabel === 'unavailable' || t.statusLabel === 'unknown' || t.statusLabel === 'failed') return 'failure'
  return 'pending'
}

/* A paused serverless database is doing what it was configured to do and
 * wakes on the next connection, so it reads as deliberately off, not broken. */
const SQL_BROKEN = new Set(['offline', 'inaccessible', 'disabled', 'unknown'])

function sqlStatusClass(d: AzureSqlDatabaseSummary): StatusTone {
  if (d.healthy) return 'success'
  if (d.statusLabel === 'paused' || d.statusLabel === 'pausing') return 'neutral'
  if (SQL_BROKEN.has(d.statusLabel)) return 'failure'
  return 'pending'
}

function OwnerTags({ repo, env, role, children }: { repo: string | null; env: string | null; role?: string | null; children?: React.ReactNode }) {
  return (
    <div className="db-tags">
      {repo ? (
        <a
          className="tag tag-accent db-repo-tag"
          href={`https://github.com/F3-Insights/${repo}`}
          target="_blank"
          rel="noreferrer noopener"
          title={`${repo} owns this database`}
        >
          {repo}
        </a>
      ) : (
        <span className="tag tag-neutral" title="No repo tag on this resource in Azure">
          unassigned
        </span>
      )}
      {env ? <span className="tag tag-neutral">{env}</span> : null}
      {role ? <span className="tag tag-neutral">{role}</span> : null}
      {children}
    </div>
  )
}

function CardFoot({ resourceGroup, portalUrl }: { resourceGroup: string | null; portalUrl: string }) {
  return (
    <div className="db-card-foot">
      {resourceGroup ? <span title="Resource group">{resourceGroup}</span> : <span />}
      <a className="db-portal-link" href={portalUrl} target="_blank" rel="noreferrer noopener">
        Portal
        <ExternalLink size={10} />
      </a>
    </div>
  )
}

function CardHead({ name, label, tone, host }: { name: string; label: string; tone: StatusTone; host: string | null }) {
  return (
    <>
      <div className="db-card-top">
        <span className="db-name" title={name}>
          {name}
        </span>
        <span className={`db-status db-status-${tone}`}>{label}</span>
      </div>
      {host ? (
        <div className="db-host" title={host}>
          {host}
        </div>
      ) : null}
    </>
  )
}

function AzureCard({ s }: { s: AzureServerSummary }) {
  const spec = [
    s.version ? `${s.engine} ${s.version}` : s.engine,
    s.tier && s.sku ? `${s.tier} ${s.sku.replace(/^Standard_/, '')}` : s.tier || s.sku,
    s.storageGb ? `${s.storageGb} GB` : null,
    s.location
  ].filter(Boolean)

  const posture = [
    s.highAvailability ? 'HA on' : 'no HA',
    s.backupRetentionDays ? `${s.backupRetentionDays}-day backups${s.geoRedundantBackup ? ', geo-redundant' : ''}` : null,
    s.publicNetworkAccess === null ? null : s.publicNetworkAccess ? 'public access' : 'private access',
    s.entraAuth ? 'Entra auth' : null
  ].filter(Boolean)

  return (
    <div className="card db-card">
      <CardHead name={s.name} label={s.statusLabel} tone={azureStatusClass(s)} host={s.host} />
      <OwnerTags repo={s.repo} env={s.env} role={s.role} />
      <div className="db-meta">{spec.join(' · ')}</div>
      {posture.length ? <div className="db-meta db-meta-quiet">{posture.join(' · ')}</div> : null}
      <CardFoot resourceGroup={s.resourceGroup} portalUrl={s.portalUrl} />
    </div>
  )
}

const KB = 1024

function bytes(n: number): string {
  if (n < KB) return `${n} B`
  if (n < KB * KB) return `${(n / KB).toFixed(1)} KB`
  return `${(n / (KB * KB)).toFixed(1)} MB`
}

function TableStoreCard({ t }: { t: AzureTableStoreSummary }) {
  const counts = t.metrics
    ? [
        t.metrics.entities !== null ? `${t.metrics.entities.toLocaleString()} entities` : null,
        t.metrics.capacityBytes !== null ? bytes(t.metrics.capacityBytes) : null,
        t.metrics.transactions24h !== null ? `${t.metrics.transactions24h.toLocaleString()} ops in 24h` : null
      ].filter(Boolean)
    : []
  const spec = [t.engine, t.sku?.replace(/^Standard_/, ''), t.location].filter(Boolean)
  const posture = [
    t.minimumTlsVersion ? `min ${t.minimumTlsVersion.replace('TLS', 'TLS ').replace('_', '.')}` : null,
    t.publicNetworkAccess === null ? null : t.publicNetworkAccess ? 'public access' : 'private access',
    t.createdAt ? `created ${rel(t.createdAt)}` : null
  ].filter(Boolean)

  return (
    <div className="card db-card">
      <CardHead name={t.name} label={t.statusLabel} tone={tableStatusClass(t)} host={t.host} />
      <OwnerTags repo={t.repo} env={t.env}>
        {t.dataClass ? (
          <span className="tag db-tag-sensitive" title="From this resource's data tag in Azure">
            {t.dataClass}
          </span>
        ) : null}
      </OwnerTags>
      {t.purpose ? <div className="db-purpose">{t.purpose}</div> : null}
      <div className="db-table-list" title="Table names only. This dashboard never reads a row.">
        {t.tables.map(name => (
          <span className="db-table-name" key={name}>
            {name}
          </span>
        ))}
      </div>
      {counts.length ? (
        <div
          className="db-meta db-counts"
          title={`Azure's hourly rollup for the whole account, not per table${
            t.metrics?.sampledAt ? `, sampled ${rel(t.metrics.sampledAt)}` : ''
          }. Counts only: no row is ever read.`}
        >
          {counts.join(' · ')}
        </div>
      ) : null}
      <div className="db-meta">{spec.join(' · ')}</div>
      {posture.length ? <div className="db-meta db-meta-quiet">{posture.join(' · ')}</div> : null}
      <CardFoot resourceGroup={t.resourceGroup} portalUrl={t.portalUrl} />
    </div>
  )
}

function SqlCard({ d }: { d: AzureSqlDatabaseSummary }) {
  const spec = [
    d.version ? `${d.engine} ${d.version}` : d.engine,
    d.tier && d.sku ? `${d.tier} ${d.sku}` : d.tier || d.sku,
    d.maxSizeGb ? `${d.maxSizeGb} GB max` : null,
    d.location
  ].filter(Boolean)
  const posture = [
    d.autoPauseMinutes ? `serverless, pauses after ${d.autoPauseMinutes} min idle` : null,
    d.publicNetworkAccess === null ? null : d.publicNetworkAccess ? 'public access' : 'private access',
    d.entraOnlyAuth ? 'Entra-only auth' : null
  ].filter(Boolean)

  return (
    <div className="card db-card">
      <CardHead name={d.server ? `${d.server} / ${d.name}` : d.name} label={d.statusLabel} tone={sqlStatusClass(d)} host={d.host} />
      <OwnerTags repo={d.repo} env={d.env} role={d.role} />
      <div className="db-meta">{spec.join(' · ')}</div>
      {posture.length ? <div className="db-meta db-meta-quiet">{posture.join(' · ')}</div> : null}
      <CardFoot resourceGroup={d.resourceGroup} portalUrl={d.portalUrl} />
    </div>
  )
}

function AzureKind<T extends { id: string }>({
  title,
  unit,
  items,
  empty,
  render
}: {
  title: string
  unit: string
  items: T[]
  empty: string
  render: (item: T) => React.ReactNode
}) {
  return (
    <section className="section">
      <div className="section-label">
        <span>{title}</span>
        <span className="count">
          · {items.length} {unit}
          {items.length === 1 ? '' : 's'}
        </span>
      </div>
      {items.length ? <div className="db-grid">{items.map(render)}</div> : <div className="empty">{empty}</div>}
    </section>
  )
}

export function Databases() {
  const [data, setData] = useState<DatabasesResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = () => {
    setLoading(true)
    setError(null)
    getDatabases()
      .then(setData)
      .catch((err: ApiError) => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const count = data?.supabase.projects.length ?? 0
  const azure = data?.azure
  const tableStores = azure?.tableStores ?? []
  const userStores = tableStores.filter(t => t.dataClass)

  return (
    <div className="shell" style={{ paddingTop: 8 }}>
      <section className="section" style={{ marginTop: 0 }}>
        <div className="section-label">
          <span>Where registered users live</span>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ marginLeft: 'auto', height: 26, padding: '0 10px' }}
            onClick={load}
            disabled={loading}
          >
            <Sync size={13} className={loading ? 'spin' : undefined} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {error ? (
          <div className="banner">
            <Warning size={16} />
            <span className="body">{error}</span>
          </div>
        ) : (
          <div className="card db-users">
            <ul className="db-users-list">
              {loading && !data ? <li>Reading Azure for stores tagged as personal data…</li> : null}
              {userStores.map(t => (
                <li key={t.id}>
                  <strong>{t.repo ?? t.name}</strong>: {t.purpose ?? t.dataClass}, in Azure Table Storage account{' '}
                  <code>{t.name}</code> (tables {t.tables.join(', ')}).
                </li>
              ))}
              <li>
                <strong>insights-portal</strong>: user accounts, in Supabase (the <code>users</code> table), used by both
                the production and staging apps.{' '}
                {data?.supabase.configured ? 'Listed under Supabase below.' : 'Listed under Supabase below once its token is set.'}
              </li>
            </ul>
            <div className="db-users-note">
              Stores appear in this list when their Azure resource carries a <code>data</code> tag. Everything here is
              read with a Reader-only credential: table names and settings, never a row.
            </div>
          </div>
        )}
      </section>

      <section className="section">
        <div className="section-label">
          <span>Supabase</span>
          {data?.supabase.configured ? (
            <span className="count">· {count} project{count === 1 ? '' : 's'}</span>
          ) : null}
        </div>

        {error ? null : loading && !data ? (
          <div className="empty">Reading Supabase project state…</div>
        ) : !data?.supabase.configured ? (
          <div className="empty">
            Not configured. Set <code>SUPABASE_ACCESS_TOKEN</code> on this deployment, a Management API
            personal access token from{' '}
            <a href="https://supabase.com/dashboard/account/tokens" target="_blank" rel="noreferrer noopener">
              supabase.com/dashboard/account/tokens
            </a>
            , to list every project this organization runs, including the one holding insights-portal's registered
            users.
          </div>
        ) : data.supabase.projects.length === 0 ? (
          <div className="empty">{data.supabase.error ?? 'No Supabase projects found for this organization.'}</div>
        ) : (
          <div className="db-grid">
            {data.supabase.projects.map(p => (
              <div className="card db-card" key={p.id}>
                <div className="db-card-top">
                  <span className="db-name">{p.name}</span>
                  <span className={`db-status db-status-${statusClass(p)}`}>{p.statusLabel}</span>
                </div>
                {p.host ? (
                  <div className="db-host" title={p.host}>
                    {p.host}
                  </div>
                ) : null}
                <div className="db-meta">
                  {p.region ? <span>{p.region}</span> : null}
                  {p.createdAt ? <span>created {rel(p.createdAt)}</span> : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {error ? null : loading && !data ? (
        <section className="section">
          <div className="section-label">Azure</div>
          <div className="empty">Reading Azure database state…</div>
        </section>
      ) : !azure?.configured ? (
        <section className="section">
          <div className="section-label">Azure</div>
          <div className="empty">
            Not configured. Set <code>AZURE_TENANT_ID</code>, <code>AZURE_CLIENT_ID</code>,{' '}
            <code>AZURE_CLIENT_SECRET</code> and <code>AZURE_SUBSCRIPTION_ID</code> on this deployment, a
            service principal holding <strong>Reader</strong> and nothing more, to list every PostgreSQL server,
            Table Storage account and SQL database this organization runs.
          </div>
        </section>
      ) : (
        <>
          {azure.error ? (
            <div className="banner" style={{ marginTop: 24 }}>
              <Warning size={16} />
              <span className="body">Azure: {azure.error}</span>
            </div>
          ) : null}
          <AzureKind
            title="Azure Postgres"
            unit="server"
            items={azure.servers}
            empty="No Azure PostgreSQL flexible servers found in this subscription."
            render={s => <AzureCard s={s} key={s.id} />}
          />
          <AzureKind
            title="Azure Table Storage"
            unit="account"
            items={tableStores}
            empty="No storage accounts holding tables found in this subscription."
            render={t => <TableStoreCard t={t} key={t.id} />}
          />
          <AzureKind
            title="Azure SQL"
            unit="database"
            items={azure.sqlDatabases ?? []}
            empty="No Azure SQL databases found in this subscription."
            render={d => <SqlCard d={d} key={d.id} />}
          />
        </>
      )}

      <section className="section">
        <div className="section-label">Coming soon</div>
        <div className="db-grid">
          {PLACEHOLDERS.map(p => (
            <div className="card db-card db-card-placeholder" key={p.key}>
              <div className="db-card-top">
                <span className="db-name">{p.title}</span>
                <span className="tag tag-neutral">planned</span>
              </div>
              <div className="db-placeholder-note">{p.note}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
